import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  QueryKey,
} from '@tanstack/react-query';

import secureRequest from '@app/helpers/secure-request';
import i18n from '@app/i18n';
import {
  describeError,
  HttpStatusError,
  toServerError,
} from '@app/i18n/describe-error';
import { toaster } from '@chakra-primitives/toaster';

interface RequestOptions {
  handleError?: boolean;
}

const handleError = async (response: Response) => {
  let json;
  try {
    json = await response.json();
  } catch (error) {
    console.warn("can't parse json response", error);
    if (!response.ok) {
      throw new HttpStatusError(response.status);
    }
    return null;
  }
  if (!response.ok) {
    throw toServerError(json?.message, response.status);
  }
  return json;
};

export async function fetchData(endpoint: string, opts: RequestOptions = {}) {
  const response = await secureRequest(endpoint, { method: 'GET' });
  if (opts.handleError) {
    return handleError(response);
  }
  if (!response.ok) {
    throw new Error('Network response was not ok - fetchData');
  }
  return response.json();
}

export async function postData(
  endpoint: string,
  body: unknown,
  opts: RequestOptions = {},
) {
  const args = {
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify(body),
  };
  const response = await secureRequest(endpoint, args);
  if (opts.handleError) {
    return handleError(response);
  }
  if (!response.ok) {
    throw new Error('Network response was not ok - postData');
  }
  return response.json();
}

export async function putData(
  endpoint: string,
  body: unknown,
  opts: RequestOptions = {},
) {
  const args = {
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'PUT',
    body: JSON.stringify(body),
  };
  const response = await secureRequest(endpoint, args);
  if (opts.handleError) {
    return handleError(response);
  }
  if (!response.ok) {
    throw new Error('Network response was not ok - putData');
  }
  return response.json();
}

export async function patchData(
  endpoint: string,
  body: unknown,
  opts: RequestOptions = {},
) {
  const args = {
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'PATCH',
    body: JSON.stringify(body),
  };
  const response = await secureRequest(endpoint, args);
  if (opts.handleError) {
    return handleError(response);
  }
  if (!response.ok) {
    throw new Error('Network response was not ok - patchData');
  }
  return response.json();
}

export async function deleteData(endpoint: string, opts: RequestOptions = {}) {
  const response = await secureRequest(endpoint, { method: 'DELETE' });
  if (opts.handleError) {
    return handleError(response);
  }
  if (!response.ok) {
    throw new Error('Network response was not ok - deleteData');
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

export interface ApiCallOptions<T> {
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: T;
}

async function apiCall<T>(options: ApiCallOptions<T>) {
  const { endpoint, method, body } = options;
  const response = await secureRequest(endpoint, {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) {
    throw toServerError(data.message);
  }
  return data;
}

export function useDynamicMutation<T>({
  dataKey,
  toasterText,
  callbackFn,
  skipToaster,
}: {
  dataKey: QueryKey;
  toasterText?: {
    success: string;
    error: string;
  };
  callbackFn?: () => void;
  skipToaster?: boolean;
}): UseMutationResult<T, Error, ApiCallOptions<T>, unknown> {
  const queryClient = useQueryClient();

  const mutation = useMutation<T, Error, ApiCallOptions<T>>(apiCall<T>, {
    onSuccess: () => {
      queryClient.invalidateQueries(dataKey);
      if (!skipToaster) {
        toaster.create({
          title: toasterText?.success || i18n.t('common:successfullyUpdated'),
          type: 'success',
        });
      }
      if (callbackFn) {
        callbackFn();
      }
    },
    onError: (error: Error) => {
      if (!skipToaster) {
        toaster.create({
          title: toasterText?.error || describeError(error),
          type: 'error',
        });
      }
    },
  });

  return mutation;
}
